import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, WandSparkles } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, type ShopStaff, type StaffAssignableRole } from "@/api";
import { sellerKeys, useSeller, useSellerStaff } from "@/api/hooks/use-sellers";
import { SellerSurvey } from "@/components/seller-survey";
import { ShopModeration } from "@/components/shop-moderation";
import { SellerPartnerPromo } from "@/components/seller-partner-promo";
import { SellerAdPoints } from "@/components/seller-ad-points";
import { StaffPanel } from "@/components/staff-panel";
import { Button } from "@osmix/ui/button";
import { Skeleton } from "@osmix/ui/skeleton";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";

// Moderation detail for a seller-shop that has no published storefront yet
// (still filling in or on moderation), reached from the admin Shops list. The
// approve/reject/block actions and the submitted profile live in
// <ShopModeration>; the intake survey in <SellerSurvey>. Sellers who already
// own a storefront are moderated on the storefront detail page instead.
export function SellerDetailPage() {
  const $ = _c(2);
  const {
    userId: t0
  } = useParams();
  const userId = t0 === undefined ? "" : t0;
  let t1;
  if ($[0] !== userId) {
    t1 = <SellerDetailContent userId={userId} showBack={true} />;
    $[0] = userId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function SellerDetailContent(t0) {
  const $ = _c(27);
  const {
    userId,
    showBack: t1
  } = t0;
  const showBack = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const sellerQuery = useSeller(userId);
  const staffQuery = useSellerStaff(userId);
  const seller = sellerQuery.data ?? null;
  const staff = staffQuery.data ?? null;
  const [actionError, setActionError] = useState(null);
  const t2 = useAuth(_temp);
  let t3;
  if ($[0] !== t2) {
    t3 = hasAdminPermission(t2, "action.catalog_import.run");
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const canImport = t3;
  const loadError = sellerQuery.error ?? staffQuery.error;
  const error = actionError ?? (loadError ? loadError.message : null);
  let t4;
  if ($[2] !== queryClient || $[3] !== userId) {
    t4 = async (memberId, role) => {
      ;
      try {
        const member = await api.sellers.updateStaffRole(userId, memberId, role);
        queryClient.setQueryData(sellerKeys.staff(userId), current => current ? {
          ...current,
          members: current.members.map(item => item.id === member.id ? member : item)
        } : current);
      } catch (t5) {
        const e = t5;
        setActionError(e instanceof Error ? e.message : String(e));
      }
    };
    $[2] = queryClient;
    $[3] = userId;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const updateStaffRole = t4;
  let t5;
  if ($[5] !== queryClient || $[6] !== userId) {
    t5 = async memberId_0 => {
      ;
      try {
        await api.sellers.removeStaffMember(userId, memberId_0);
        queryClient.setQueryData(sellerKeys.staff(userId), current_0 => current_0 ? {
          ...current_0,
          members: current_0.members.filter(item_0 => item_0.id !== memberId_0)
        } : current_0);
      } catch (t6) {
        const e_0 = t6;
        setActionError(e_0 instanceof Error ? e_0.message : String(e_0));
      }
    };
    $[5] = queryClient;
    $[6] = userId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const removeStaffMember = t5;
  let t6;
  if ($[8] !== showBack || $[9] !== t) {
    t6 = showBack && <Button variant="outline" size="sm" asChild={true}><Link to="/shops"><ArrowLeft className="size-4" /> {t("storefronts.back")}</Link></Button>;
    $[8] = showBack;
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== error) {
    t7 = error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>;
    $[11] = error;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== error || $[14] !== seller) {
    t8 = !seller && !error && <div className="space-y-4"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-48 w-full" /></div>;
    $[13] = error;
    $[14] = seller;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== canImport || $[17] !== removeStaffMember || $[18] !== seller || $[19] !== staff || $[20] !== updateStaffRole) {
    t9 = seller && <><header className="flex flex-wrap items-start justify-between gap-3"><div className="min-w-0 space-y-1"><h1 className="text-2xl font-semibold">{seller.shopName}</h1><p className="text-sm text-muted-foreground">{seller.sellerName} · {seller.email}</p></div>{canImport && <Button variant="outline" size="sm" asChild={true}><Link to={`/catalog-import?shopId=${encodeURIComponent(seller.shopId ?? seller.id)}`}><WandSparkles className="size-4" />Загрузить каталог (ИИ)</Link></Button>}</header><ShopModeration userId={seller.id} /><SellerPartnerPromo userId={seller.id} /><SellerAdPoints userId={seller.id} /><StaffPanel staff={staff} editable={true} onRoleChange={updateStaffRole} onRemove={removeStaffMember} /><SellerSurvey userId={seller.id} /></>;
    $[16] = canImport;
    $[17] = removeStaffMember;
    $[18] = seller;
    $[19] = staff;
    $[20] = updateStaffRole;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t10 = <div className="space-y-6">{t6}{t7}{t8}{t9}</div>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
function _temp(s) {
  return s.user;
}